import {
  createContext,
  useState,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
  useContext,
  ReactNode,
} from "react";
import axios from "axios";
import toast from "react-hot-toast";
import OneSignal from "react-onesignal";
import { setSentryUser } from "../lib/sentry";

interface PhoneNumber {
  type: string;
  _id: string;
  createdAt: string;
  updatedAt: string;
  id: string;
  value?: string;
}

interface Email {
  value: string;
  type: string;
  primary: boolean;
  _id: string;
  createdAt: string;
  updatedAt: string;
  id: string;
}

interface Name {
  familyName: string;
  givenName: string;
  _id: string;
  createdAt: string;
  updatedAt: string;
  id: string;
}

export interface UserInfo {
  _id: string;
  email_verified: boolean;
  mobile_no_verified: boolean;
  status: string;
  is_first_login: boolean;
  is_deleted: boolean;
  onboarding_method: string;
  phoneNumbers: PhoneNumber[];
  emails: Email[];
  userName: string;
  user_id: string;
  name: Name;
  createdAt: string;
  updatedAt: string;
  __v: number;
  last_login?: string;
  profile_image?: string;
  id: string;
  user_group_map: UserGroupMap[];
  user_permissions: string[];
}

export interface UserGroupMap {
  _id: string;
  permissions: string[];
  roles: { role_key: string; role_name: string }[];
  user_id: string;
  group_id: string;
  createdAt: string;
  updatedAt: string;
}

interface UserContextProps {
  userInfo: UserInfo | null;
  setUserInfo: Dispatch<SetStateAction<UserInfo | null>>;
  fetchUserInfo: () => Promise<void>;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
}

const defaultUserContext: UserContextProps = {
  userInfo: null,
  setUserInfo: () => {},
  fetchUserInfo: async () => {},
  isLoading: false,
  isInitialized: false,
  error: null,
};

export const UserContext = createContext<UserContextProps>(defaultUserContext);

export const useUser = () => {
  return useContext(UserContext);
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userInfo && userInfo.user_id) {
      console.log(`🔒 Logging in user ${userInfo.user_id} to OneSignal...`);
      OneSignal.login(userInfo.user_id);

      // Set Sentry user context
      const primaryEmail = userInfo.emails?.find((e) => e.primary)?.value || userInfo.emails?.[0]?.value;
      const fullName = userInfo.name ? `${userInfo.name.givenName} ${userInfo.name.familyName}`.trim() : userInfo.userName;
      setSentryUser({ id: userInfo.user_id, email: primaryEmail, name: fullName });
    } else {
      console.log("🔓 Logging out user from OneSignal...");
      OneSignal.logout();

      // Clear Sentry user context
      setSentryUser(null);
    }
  }, [userInfo]);

  const fetchUserInfo = useCallback(async () => {
    const accessToken = localStorage.getItem("auth_token");

    if (!accessToken) {
      console.warn("No access token found in localStorage");
      setError("No access token found");
      setIsInitialized(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${import.meta.env.VITE_DMS_API}/dms-v2-srv/user-srv/user-info`,
        {
          headers: {
            accept: "*/*",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data?.success) {
        const userData = response.data.data;
        setUserInfo(userData);
        setError(null);
      } else {
        const errorMessage = "Failed to fetch user information";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error fetching user info:", error);
      if (error?.response?.data?.error?.error_description) {
        toast.error(error?.response?.data?.error?.error_description);
        localStorage.removeItem("auth_token");
        setTimeout(() => {
          window.location.href = "/session-expired";
        }, 2000); // wait 2 seconds before redirect
      } else if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
        localStorage.removeItem("auth_token");
        setTimeout(() => {
          window.location.href = "/session-expired";
        }, 2000);
      } else {
        toast.error("An unexpected error occurred");
        localStorage.removeItem("auth_token");
        setTimeout(() => {
          window.location.href = "/session-expired";
        }, 2000);
      }

      if (error.response?.status === 401 || error.response?.status === 403) {
        setError("Session expired. Please login again.");
        // Only redirect if we're not on the login flow (to prevent interrupting TokenHandler)
        const isOnLoginFlow =
          window.location.pathname.includes("/dataloader") ||
          window.location.pathname.includes("/login") ||
          window.location.pathname.includes("/session-expired");

        if (!isOnLoginFlow) {
          toast.error("Session expired. Please login again.");
          localStorage.removeItem("auth_token");
          window.location.href = "/session-expired";
        } else {
          console.log("⚠️ Auth error during login flow, skipping redirect");
        }
      }
      // else {
      //   const errorMessage =
      //     error.response?.data?.message ||
      //     error.message ||
      //     "An unexpected error occurred";
      //   setError(errorMessage);
      //   toast.error(errorMessage);
      // }
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, []); // Remove navigate dependency

  // Initialize user info on mount (only if not on login flow)
  useEffect(() => {
    const accessToken = localStorage.getItem("auth_token");
    const isOnLoginFlow =
      window.location.pathname.includes("/dataloader") ||
      window.location.pathname.includes("/handle/token");

    if (accessToken && !isOnLoginFlow) {
      console.log("🔄 UserContext: Token found on mount, fetching user info");
      fetchUserInfo();
    } else {
      setIsInitialized(true);
      if (isOnLoginFlow) {
        console.log(
          "ℹ️ UserContext: On login flow, skipping auto-fetch (TokenHandler will handle it)"
        );
      }
    }
  }, []); // Run only once on mount

  // Listen for token changes (from other tabs via storage event, or same window via custom event)
  useEffect(() => {
    const handleStorageChange = () => {
      const accessToken = localStorage.getItem("auth_token");
      if (accessToken && !userInfo && isInitialized) {
        console.log(
          "🔄 UserContext: Token changed in other tab, fetching user info"
        );
        fetchUserInfo();
      } else if (!accessToken && userInfo) {
        setUserInfo(null);
        setIsInitialized(true);
      }
    };

    const handleCustomTokenChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.token) {
        console.log(
          "🔄 UserContext: Token changed in same window via custom event"
        );
        // Don't fetch here - TokenHandler will call fetchUserInfo directly
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("auth_token_changed", handleCustomTokenChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("auth_token_changed", handleCustomTokenChange);
    };
  }, [userInfo, isInitialized, fetchUserInfo]);

  const value: UserContextProps = {
    userInfo,
    setUserInfo,
    fetchUserInfo,
    isLoading,
    isInitialized,
    error,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
